import config from 'cityads-v3/config'
import { FormState, email, notEmpty, useForm } from "@xvii/useform"
import { RootState } from "cityads-v3/types/reducer"
import { useCallback, useMemo } from "react"
import { useTranslation } from "react-i18next"
import { useDispatch, useSelector } from "react-redux"
import {
  submitRegisterAdvertiserForm,
} from 'cityads-v3/actions/auth'
import { usePreloadRegions } from 'cityads-v3/utils/useUtils'
import { omit } from 'lodash'

export function useRegisterAdvertiserForm() {
  usePreloadRegions();
  const { t } = useTranslation(['main'])
  const dispatch = useDispatch()

  const defaultField = {
    defaultValue: '',
    valueType: String(),
    validators: [notEmpty({ errorText: t('forms.errorFieldNoEmpty') })],
    validateOnChange: true
  }

  const form = useForm({
    onSubmit: useCallback(async (data, signal, form) => {
      form.setState(FormState.SENDING);

      try {
        await dispatch(
          submitRegisterAdvertiserForm(
            omit({
              ...data,
              'g-recaptcha-response': data.captchaToken
            }, 'captchaToken')
          )
        )

        form.setState(FormState.SUCCESS)
      } catch (err) {
        form.setState(FormState.FAIL)
      }

    }, [dispatch]),
    fields: {
      first_name: { ...defaultField },
      last_name: { ...defaultField },
      company: { ...defaultField },
      phone: { ...defaultField },
      email: {
        ...defaultField,
        validators: [
          notEmpty({ errorText: t('forms.errorFieldNoEmpty') }),
          email({ errorText: t('forms.errorFieldEmail') })
        ],
      },
      url: { ...defaultField },
      industry: {
        ...defaultField,
        defaultValue: 0,
        valueType: Number(),
      },
      country: {
        ...defaultField,
        defaultValue: 0,
        valueType: Number(),
      },
      comment: { ...defaultField },
      agreement: {
        ...defaultField,
        defaultValue: false,
        valueType: Boolean(),
      },
      captchaToken: {
        defaultValue: '',
        valueType: String(),
      }
    }
  })

  const handleSendForm = useCallback(() => {
    if (form.validate()) {
      if (config.RECAPTCHA_IGNORE) {
        form.fields.captchaToken.setValue('developer-mode');
        form.handleSubmit()
      } else {
        window?.grecaptcha.execute();
      }
    }
  }, [form])

  const handleRecaptchaCallback = useCallback((token: string) => {
    form.fields.captchaToken.setValue(token);
    form.handleSubmit()
  }, [form])

  return {
    form,
    handleSendForm,
    handleRecaptchaCallback,
    countries: useCountries(),
    industries: useIndustries(),
    language: useSelector((state: RootState) => state.language.language)
  }
}


function useIndustries() {
  const { t } = useTranslation(['main'])

  return useMemo(() => ([
    { value: 0, name: '—' },
    { value: 4299744, name: t('registration.industries.eCommerce') },
    { value: 4413486, name: t('registration.industries.travel') },
    { value: 4299746, name: t('registration.industries.finance') },
    { value: 4413494, name: t('registration.industries.realEstate') },
    { value: 4299748, name: t('registration.industries.gamesWeb') },
    { value: 4413480, name: t('registration.industries.gamesMobile') },
    { value: 4299750, name: t('registration.industries.mobile') },
    { value: 4413482, name: t('registration.industries.classifieds') },
    { value: 4413490, name: t('registration.industries.jobAndEducation') },
    { value: 4413492, name: t('registration.industries.subscriptionsAndPayments') },
    { value: 4413496, name: t('registration.industries.entertainment') },
    { value: 4413484, name: t('registration.industries.gambling') },
    { value: 4413488, name: t('registration.industries.other') },
  ]), [t])
}


function useCountries() {
  const regionsById = useSelector((state: RootState) => state.offers.regionsById);

  return useMemo(() => (
    Object.keys(regionsById)
      .map(key => ({
        name: regionsById[key].name,
        value: regionsById[key].id,
        children: regionsById[key].items,
      }))
      // Мы получаем дерево с глубиной до стран. У стран нет детей, у более высоких уровней (Весь мир, Европа, СНГ) - есть. Мы пропускаем эти уровни.
      .filter(region => !region.children)
  ), [regionsById])
}


export function useSearchForm() {
  return useForm({
    fields: {
      country: { defaultValue: '', valueType: String() }
    }
  })
}
